import { ChevronLeftIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Grid,
  Link,
  Skeleton,
  Stack,
  Text,
  useTheme,
  Switch,
} from "@chakra-ui/react";
import {
  AvatarImage,
  MessageBox,
  scrollToTop,
  useAuth,
  VeriDrawer,
} from "@verivest/shared-components";
import Button from "@verivest/shared-components/src/buttons/Button";
import { HubSpotCalendarPopup } from "@verivest/shared-components/src/components/HubSpotCalendarPopup";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  DEFAULT_IMPERSONATING_HEADER_HEIGHT,
  ImpersonatingHeader,
} from "../investor-portal/headers/ImpersonatingHeader";
import { ManagerIcon } from "../investor-portal/headers/ManagerIcon/ManagerIcon";
import VeriHeading from "../VeriHeading";
import { OnboardingContainerProps } from "./Container.types";
import { getChatPosition } from "@verivest/shared-components/src/utils";

const ContainerHeader = ({
  onClickExit,
  toExit,
  toExitLabel,
}: {
  onClickExit?: () => void;
  toExit?: string;
  toExitLabel?: string;
}) => {
  return (
    <Flex
      as="nav"
      align="center"
      justify="space-between"
      wrap="nowrap"
      height="64px"
      bg="white"
      padding="none"
      boxShadow="0px 2px 4px rgba(0, 0, 0, 0.06), inset 0px -1px 0px #E5E5E5"
    >
      <Flex align="center" ml={5} mr={5} height="24px">
        <ManagerIcon onClick={onClickExit} />
      </Flex>

      <Flex mr={5}>
        {(onClickExit || toExit) && (
          <Link
            as={toExit ? RouterLink : Link}
            color="brand.main"
            to={toExit}
            onClick={onClickExit}
            textStyle="bodyRegular"
          >
            {toExitLabel || "Save and exit"}
          </Link>
        )}
      </Flex>
    </Flex>
  );
};

const OnboardingContainer: React.FC<OnboardingContainerProps> = ({
  title,
  subtitle,
  centerTitles,
  children,
  onBack: userOnBack,
  onSubmit: userOnSubmit,
  submitText = "Continue",
  onSkip,
  skipTo,
  isSubmit,
  isSubmitDisabled,
  onSubmitForm,
  isSubmitting,
  noBottom,
  message,
  messageType = "error",
  rightExtra,
  full,
  fullLoading,
  fullMaxSize = "100%",
  toExit,
  leftButtons,
  rightButtons,
  toExitLabel,
  onClickExit,
  asDrawer,
  drawerNoAnimation = true,
  backLabel = "Back",
  onClose,
  hideHelpBox,
  helperBoxSubtitle = "Schedule some time with us",
  helperBoxUrl = "mailto:support@verivest.com",
  flexWrapperProps,
  size,
}) => {
  const theme = useTheme();
  const { auth } = useAuth();
  const [isScrolledBottom, setIsScrolledBottom] = React.useState(true);
  const [emailNotifyEnabled, setEmailNotifyEnabled] = React.useState(false);

  const watchScroll = () =>
    setIsScrolledBottom(
      window.innerHeight + window.scrollY >= document.body.offsetHeight,
    );

  const onSubmit = () => {
    userOnSubmit?.();
    scrollToTop();
  };

  const onBack = () => {
    userOnBack?.();
    scrollToTop();
  };

  React.useEffect(() => {
    window.addEventListener("scroll", watchScroll);

    watchScroll();

    return () => {
      window.removeEventListener("scroll", watchScroll);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const footerShadow =
    !asDrawer &&
    "inset 0px -1px 0px #DCE1E7" +
      (!isScrolledBottom ? ", 0px -25px 25px -25px rgba(0, 0, 0, 0.19)" : "");

  const maxWidth = full ? fullMaxSize : "840px";

  const chatPosition = getChatPosition();
  const chatGap = chatPosition?.width ? chatPosition.width : 0;

  const buttons = (
    <>
      {!noBottom && (
        <Box
          {...(asDrawer
            ? {}
            : {
                zIndex: "aboveChakraOverlay",
                position: "fixed",
                left: "0",
                right: "0",
                bottom: "0",
              })}
          backgroundColor="white"
          width="100%"
        >
          <Grid
            bg="white"
            templateColumns={{
              base: "1fr",
              md: full || asDrawer ? "1fr" : "5fr 4fr",
            }}
            height={!asDrawer ? "72px" : "auto"}
          >
            <Flex justifyContent="center">
              <Box maxWidth={maxWidth} width={{ base: "100%", md: "100%" }}>
                <Flex justifyContent="center" px={!asDrawer ? "16px" : 0}>
                  <Box maxWidth={maxWidth} width={{ base: "100%" }}>
                    <Flex
                      flexWrap="wrap"
                      alignItems="center"
                      justifyContent="space-between"
                      height={!asDrawer ? "72px" : "auto"}
                      borderTop={!asDrawer ? "1px solid" : undefined}
                      borderTopColor={!asDrawer ? "border.grey" : undefined}
                      boxShadow={footerShadow || undefined}
                      transition="box-shadow 0.3s"
                    >
                      <Flex>
                        {onBack && (
                          <Button
                            isLarge
                            empty
                            leftIcon={ChevronLeftIcon}
                            leftIconProps={{ boxSize: "24px" }}
                            onClick={onBack}
                          >
                            {backLabel || "Back"}
                          </Button>
                        )}
                        {leftButtons}
                      </Flex>
                      <Flex>
                        {rightButtons}
                        {(onSkip || skipTo) && (
                          <Button
                            isLarge
                            empty
                            mr="24px"
                            onClick={onSkip}
                            to={skipTo}
                          >
                            Skip for now
                          </Button>
                        )}
                        {asDrawer && (
                          <Button
                            isLoading={isSubmitting}
                            isLarge
                            onClick={onClose}
                            outline
                            mr={2}
                          >
                            Cancel
                          </Button>
                        )}
                        {(onSubmit || isSubmit) && (
                          <Box mr={chatGap ? `${chatGap}px` : undefined}>
                            <Button
                              data-testid="submitButton"
                              isLoading={isSubmitting}
                              isLarge
                              type={isSubmit ? "submit" : undefined}
                              onClick={onSubmit}
                              isDisabled={isSubmitDisabled}
                            >
                              {submitText}
                            </Button>
                          </Box>
                        )}
                      </Flex>
                    </Flex>
                  </Box>
                </Flex>
              </Box>
            </Flex>
            {!asDrawer && <Box bg="bg.grey"></Box>}
          </Grid>
        </Box>
      )}
    </>
  );

  if (asDrawer && onClose) {
    return (
      <VeriDrawer
        size={size || "4xl"}
        isOpen={true}
        onClose={onClose}
        title={title}
        footer={buttons}
        variant={"form"}
        noAnimation={drawerNoAnimation}
        closeOnOverlayClick={false}
        modalAsDrawer
      >
        {children}
      </VeriDrawer>
    );
  }

  const isMailTo = String(helperBoxUrl || "").match(/^mailto:/);
  const isImpersonating = auth.isManagerImpersonating();

  return (
    <>
      {isImpersonating && (
        <Box
          h={`${
            theme.investorPortal.headerHeights.impersonatingHeader ||
            DEFAULT_IMPERSONATING_HEADER_HEIGHT
          }px`}
        >
          <Box position={"fixed"} left={0} right={0} top={0}>
            <ImpersonatingHeader />
          </Box>
        </Box>
      )}
      <ContainerHeader
        toExit={toExit}
        toExitLabel={toExitLabel}
        onClickExit={onClickExit}
      />
      <Flex align="center" justify="flex-end" p={4}>
        <Flex align="center">
          <Text mr={3}>Email Notifications</Text>
          <Switch
            isChecked={emailNotifyEnabled}
            onChange={(e) => setEmailNotifyEnabled(e.target.checked)}
            colorScheme="brand"
          />
        </Flex>
      </Flex>
      <Grid
        bg="white"
        templateColumns={{ base: "1fr", md: full ? "1fr" : "5fr 4fr" }}
        minHeight="calc(100vh - 64px)"
      >
        <Flex justifyContent="center" mb="90px" {...flexWrapperProps}>
          <Box maxWidth={maxWidth} width={"100%"}>
            <form style={{ height: "100%" }} onSubmit={onSubmitForm}>
              {message && (
                <Box
                  px={{ base: "20px", md: "60px" }}
                  pt={{ base: "20px", md: "48px" }}
                >
                  <MessageBox type={messageType} text={message} />
                </Box>
              )}
              {fullLoading && (
                <Flex
                  flexDirection="column"
                  px={{ base: "20px", md: "60px" }}
                  py={{ base: "20px", md: "48px" }}
                >
                  <Stack>
                    <Skeleton height="40px" minWidth="60px" maxWidth="120px" />
                    <Skeleton height="20px" />
                    <Skeleton height="20px" />
                  </Stack>
                </Flex>
              )}
              {!fullLoading && (
                <Flex
                  flexDirection="column"
                  height="100%"
                  px={{ base: "20px", md: "60px" }}
                  py={{ base: "20px", md: "48px" }}
                >
                  {!!(title || subtitle) && (
                    <Flex
                      flexDirection="column"
                      alignItems={(centerTitles && "center") || undefined}
                    >
                      {title && (
                        <VeriHeading
                          textStyle="h1"
                          textAlign={centerTitles && "center"}
                          fontSize="32px"
                          as="h1"
                          mb={subtitle ? "8px" : "32px"}
                        >
                          {title}
                        </VeriHeading>
                      )}
                      {subtitle && (
                        <Box
                          textAlign={centerTitles ? "center" : "initial"}
                          mb="32px"
                        >
                          {subtitle}
                        </Box>
                      )}
                    </Flex>
                  )}

                  {children}
                </Flex>
              )}
              <Flex flex={1}></Flex>

              {buttons}
            </form>
          </Box>
        </Flex>
        {!full && (
          <Box bg="bg.grey">
            <Box
              mx={{ base: "16px", md: "48px" }}
              mt={{ base: "48px", md: "64px" }}
              mb="90px"
            >
              {rightExtra}
              {rightExtra && !hideHelpBox && (
                <Box
                  borderTopColor="border.input"
                  borderTopWidth="1px"
                  width="full"
                  my="32px"
                ></Box>
              )}
              {!hideHelpBox && (
                <Flex>
                  <AvatarImage
                    h="48px"
                    w="48px"
                    minWidth="48px"
                    minHeight="48px"
                    image={"/images/team/Nicole.jpg"}
                  />
                  <Box width="full" ml="10px" textAlign="left">
                    <Text>Have questions? We&#39;re here to help.</Text>
                    {isMailTo ? (
                      <Link
                        href={helperBoxUrl}
                        color={"#4d5055"}
                        fontWeight={"bold"}
                        isExternal
                      >
                        {helperBoxSubtitle}
                      </Link>
                    ) : (
                      <HubSpotCalendarPopup data-private>
                        <Link color={"#4d5055"} fontWeight={"bold"}>
                          {helperBoxSubtitle}
                        </Link>
                      </HubSpotCalendarPopup>
                    )}
                  </Box>
                </Flex>
              )}
            </Box>
          </Box>
        )}
      </Grid>
    </>
  );
};

export default OnboardingContainer;
